/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Component, EventEmitter, Inject, OnInit, Output, ViewChild } from '@angular/core';
import {
  IPanelDataFacade,
  PANEL_DATA_FACADE_TOKEN,
} from '../../../../../../data-access/src/lib/domain/facades-models/panel-data-facade-interface';
import { Subscription } from 'rxjs';
import { PanelCircuitDataReturnCodes } from '../../../models/panel-circuit-data-return-codes';
import { CircuitList } from '../../../models/paneldata-info';
import { SelectedPanelDataItem } from '../../../models/selected-panel-data-item';
import { GlobalConstants } from '../../../constants/global-constants';
import { DxSelectBoxComponent } from 'devextreme-angular';
import { AlertStatus, IAlert } from "../../../../../../../shared/ui/src/lib/components/alert/alert-types";

@Component({
  selector: 'sysque-apply-panel-data',
  templateUrl: './apply-panel-data.component.html',
  styleUrls: ['./apply-panel-data.component.scss'],
  standalone: false
})
export class ApplyPanelDataComponent implements OnInit {
  @Output() close = new EventEmitter();
  public componentTitle = '';
  public projectList: string[] = [];
  public panelList: string[] = [];
  public circuitList: CircuitList[] = [];
  public project: string = '';
  public panel: string;
  public isWarning: boolean = false;
  public validSelectionSubscription: Subscription;
  public isApplyButtonDisabled: boolean = true;
  public checkCircuits: string[] = [];
  public projectIndex: number = 0;
  public isShowWindow: boolean = false;
  public selectedItem: SelectedPanelDataItem;
  public selectedCircuits: CircuitList[] = [];
  public warningMessage: string;
  public alertStatus: AlertStatus;
  public showDismissBtn: boolean = true;
  public isDropDownOpened: boolean = false;
  public isProjectDropDownOpened: boolean = false;
  public selectedPanel: string = '';
  public get alert(): IAlert {
    return {
      message: this.warningMessage,
      type: this.alertStatus,
      isDismissable: this.showDismissBtn,
    };
  }

  @ViewChild('panelSelectBoxRef', { static: false }) selectBox: DxSelectBoxComponent;
  constructor(
    @Inject(PANEL_DATA_FACADE_TOKEN) private panelDataFacade: IPanelDataFacade
  ) {}

  /**
   * Initializes the component.
   * - Sets the component title to 'Apply Panel / Circuit ID'.
   * - Updates the localStorage values for panel data response, message title, and message content.
   * - Adds an event listener for the 'storage' event.
   * - Calls the 'initializeProjectData' method.
   */
  public ngOnInit(): void {
    this.componentTitle = 'Apply Panel / Circuit ID';
    this.panelDataFacade.localStorageUpdate(GlobalConstants.PANEL_DATA_RESPONSE, '');
    this.panelDataFacade.localStorageUpdate(GlobalConstants.MSG_TITLE, '');
    this.panelDataFacade.localStorageUpdate(GlobalConstants.MSG_CONTENT, '');
    window.addEventListener('storage', this.handleStorageEvent.bind(this));
    this.panelDataFacade.localStorageUpdate(GlobalConstants.SHOW_ACRYLIC_SURFACE, true);
    this.initializeProjectData();
  }

  /**
   * Handles the storage event.
   *
   * @param event - The storage event.
   */
  public handleStorageEvent(event: StorageEvent): void {
    if (event.key === GlobalConstants.PANEL_DATA_RESPONSE) {
      if (event.newValue === '') {
        return;
      }
      const response = JSON.parse(event.newValue);
      if (response == PanelCircuitDataReturnCodes.NoSelection) {
        this.showSysqueAlert(GlobalConstants.SelectFittingToApply, AlertStatus.Warning);
      } else if (response == PanelCircuitDataReturnCodes.Success) {
        this.showDismissBtn = false;
        this.showSysqueAlert(GlobalConstants.SelectApplyPanelSuccessfully, AlertStatus.Success);
        this.isApplyButtonDisabled = true;
        setTimeout(() => {
          this.closeWindow();
        }, 2000);
        this.panelDataFacade.localStorageUpdate(GlobalConstants.SHOW_ACRYLIC_SURFACE, false);
      }
    }
  }

  /**
   * Lifecycle hook that is called when the component is about to be destroyed.
   * It removes the event listener for the 'storage' event.
   */
  public ngOnDestroy(): void {
    window.removeEventListener('storage', this.handleStorageEvent.bind(this));
  }

  /**
   * Handles the value change event.
   *
   * @param event - The value change event.
   * @param circuitItemChecked - The checked circuit item.
   */
  public onValueChanged(event: any, circuitItemChecked: any): void  {
    const circuitName = circuitItemChecked.circuitName;
    if (event.detail) {
      this.addCircuitIfNotPresent(circuitName);
    } else {
      this.removeCircuit(circuitName);
    }
  }

  /**
   * Displays a Sysque alert with the specified message and alert type.
   *
   * @param message - The message to be displayed in the alert.
   * @param alertStatus - The type of the alert (e.g., 'success', 'warning', 'error').
   */
  public showSysqueAlert(message: string, alertStatus: AlertStatus): void  {
    this.windowResize(540);
    this.warningMessage = message;
    this.alertStatus = alertStatus;
  }

  /**
   * Handles the click event when the dismiss button is clicked.
   */
  public onDismissClick(): void  {
    this.windowResize(480);
    this.warningMessage = '';
  }

  /**
   * Adds a circuit to the list of checked circuits if it is not already present.
   * @param circuitName - The name of the circuit to add.
   */
  public addCircuitIfNotPresent(circuitName: string): void  {
    if (!this.checkCircuits.includes(circuitName)) {
      this.checkCircuits.push(circuitName);
      this.isApplyButtonDisabled = false;
    }
  }

  /**
   * Removes a circuit from the checkCircuits array.
   * @param circuitName - The name of the circuit to be removed.
   */
  public removeCircuit(circuitName: string): void {
    const circuitIndex = this.checkCircuits.indexOf(circuitName);
    if (circuitIndex !== -1) {
      this.checkCircuits.splice(circuitIndex, 1);
    }
    if (this.checkCircuits.length === 0) {
      this.isApplyButtonDisabled = true;
    }
  }

  /**
   * Closes the window and performs necessary actions.
   * - Closes the panel data facade.
   * - Emits the close event.
   * - Hides the acrylic surface service.
   */
  public closeWindow(): void  {
    this.panelDataFacade.closeWindow();
    this.close.emit();
    this.panelDataFacade.localStorageUpdate(GlobalConstants.SHOW_ACRYLIC_SURFACE, false);
  }

  /**
   * Resizes the window and triggers the `windowResize` method of the `panelDataFacade`.
   * @param alertHeight - The height of the alert.
   */
  public windowResize(alertHeight: number): void  {
    this.panelDataFacade.windowResize(alertHeight);
  }

  /**
   * Handles the event when the project value changes.
   *
   * @param event - The event object containing the new value.
   */
  public onProjectValueChanged(event: any): void  {
    if (this.isProjectDropDownOpened) {
      this.panel = '';
      this.checkCircuits = [];
      this.panelList = [];
      this.selectedPanel = '';
      this.isWarning = false;
      this.isApplyButtonDisabled = true;
      this.isProjectDropDownOpened = false;
      this.resetDropDown();
      this.project = event.value;
      this.getPanelData(false);
    }
  }

  /**
   * Resets the drop-down component.
   * If the drop-down is dirty, it sets the 'isDirty' option to false.
   * If the panel is not present, it resets the drop-down instance.
   * Finally, it closes the drop-down.
   * If the drop-down is already opened, it also closes it.
   */
  public resetDropDown(): void {
    if (this.selectBox.instance.option('isDirty')) {
      this.selectBox.instance.option('isDirty', false);
      this.selectBox.instance.reset();
      this.selectBox.instance.close();
    }
    if (this.isDropDownOpened) {    
      this.selectBox.instance.reset();
    }
    this.selectBox.instance.close();
   
  }

  /**
   * Handles the event when a project is opened.
   */
  public onProjectOpened(): void {
    this.isProjectDropDownOpened = true;
    this.resetDropDown();
  }

  /**
   * Called when the panel is opened.
   */
  public onPanelOpened(): void {
    this.isDropDownOpened = true;
  }

  /**
   * Handles the event when the panel value is changed.
   *
   * @param event - The event object containing the new value.
   */
  public onPanelValueChanged(event: any): void  {
    if (this.isDropDownOpened) {
      this.panel = event.value;
      this.checkCircuits = [];
      this.isApplyButtonDisabled = true;
      this.isWarning = false;
      if (this.panel !== undefined && this.panel !== null && this.panel !== '') {
        this.getCircuitData(false);
        this.windowResize(480);
      }
    }
    this.selectBox.instance.option('isDirty', true);
  }

  /**
   * Retrieves panel data asynchronously.
   *
   * @param initialize - A boolean indicating whether to initialize the panel data.
   * @returns A promise that resolves to void.
   */
  public async getPanelData(initialize: boolean): Promise<void> {
    this.panel = '';
    this.panelList = await this.panelDataFacade.getPanelData(this.project, initialize);
    if (this.panelList && this.panelList.length > 0) {
      if (initialize && this.isPanelNameValid()) {
        this.setActivePanel();
        this.removeActivePanelFromList();
        await this.getCircuitData(true);
      } else {
        this.panelList.splice(0, 1);
        this.circuitList = [];
      }
    } else {
      this.panelList = [];
      this.circuitList = [];
    }
  }

  /**
   * Retrieves circuit data asynchronously.
   *
   * @returns A promise that resolves to void.
   */
  public async getCircuitData(initialize: boolean): Promise<void> {
    this.isWarning = false;
    if (this.isProjectAndPanelValid()) {
      this.circuitList = [];
      this.circuitList = await this.panelDataFacade.getCircuitData(this.project, this.panel);
      this.toggleCircuitSelection(initialize);
    }
  }

  /**
   * Updates the circuit check and button status.
   *
   * This method filters the circuit list to get the selected circuits.
   * If there are selected circuits, it adds their circuit names to the checkCircuits array and enables the apply button.
   */
  public updateCircuitCheckAndButtonStatus(): void  {
    const selectedCircuits = this.circuitList.filter((x) => x.isSelected);
    if (selectedCircuits.length > 0) {
      this.checkCircuits.push(...selectedCircuits.map((y) => y.circuitName.toString()));
      this.isApplyButtonDisabled = false;
    }
  }

  /**
   * Saves the data by populating selected circuits, preparing the selected item, and applying panel data.
   */
  public saveData(): void {
    this.populateSelectedCircuits();
    this.prepareSelectedItem();
    this.applyPanelData();
  }

  /**
   * Displays an alert based on the provided response.
   * 
   * @param response - The response string.
   * @returns void
   */
  public showAlert(response: string): void {
    let componentAlertTitle = '';
    let msgContent = '';
    switch (JSON.parse(response)) {
      case PanelCircuitDataReturnCodes.NoSelection:
        componentAlertTitle = GlobalConstants.SelectApplyPanelSelectionRequired;
        msgContent = GlobalConstants.SelectApplyPanelSelectionStructure;
        break;
      case PanelCircuitDataReturnCodes.SelectionMismatch:
        componentAlertTitle = GlobalConstants.SelectApplyPanelSelectionMismatch;
        msgContent = GlobalConstants.SelectApplyPanelSelectConduit;
        break;
      default:
        break;
    }
    this.panelDataFacade.localStorageUpdate(GlobalConstants.MSG_TITLE, componentAlertTitle);
    this.panelDataFacade.localStorageUpdate(GlobalConstants.MSG_CONTENT, msgContent);
  }

  private toggleCircuitSelection(initialize: boolean): void  {
    if (initialize) {
      this.updateCircuitCheckAndButtonStatus();
    } else {
      this.circuitList.forEach((circuit) => {
        circuit.isSelected = false;
      });
    }
  }

  private isProjectAndPanelValid(): boolean {
    return this.project !== '' && this.panel !== '';
  }

  private populateSelectedCircuits(): void {
    this.checkCircuits.forEach((circuit) => {
      circuit = circuit.trim();
      this.selectedCircuits.push({ circuitName: circuit, isSelected: true });
    });
  }

  private prepareSelectedItem(): void {
    this.selectedItem = {
      selectedProjectName: this.project,
      selectedPanelName: this.panel,
      selectedCircuitLists: this.selectedCircuits,
      selectedSystemName: '',
      selectedCategory: '',
      selectedWireFillSpec: '',
    };
  }

  private applyPanelData(): void {
    if (this.selectedCircuits.length > 0) {
      this.panelDataFacade.applyPanelData(this.selectedItem);
    }
  }

  private isPanelNameValid(): boolean {
    const panelName = this.panelList[0];
    return panelName && panelName !== '';
  }

  private setActivePanel(): void {
    this.panel = this.panelList[0];
  }

  private removeActivePanelFromList(): void {
    const panelIndex = this.panelList.findIndex((panel) => panel === this.panel);
    this.panelList.splice(panelIndex, 2);
    const panelIndexAfterSplit = this.panelList.findIndex((panel) => panel === this.panel);
    this.selectedPanel = this.panelList[panelIndexAfterSplit];
  }

  private async initializeProjectData(): Promise<void> {
    this.projectList = await this.panelDataFacade.initializeProjectData();
    this.handleProjectList();
  }

  private handleProjectList(): void {
    if (this.isProjectNameValid()) {
      this.project = this.projectList[0];
      this.removeProjectFromList();
      this.getPanelData(true);
    } else {
      this.resetPanelData();
    }
  }

  private isProjectNameValid(): boolean {
    const projectName = this.projectList[0];
    return this.projectList.length > 0 && projectName && projectName !== '' && projectName !== '--Select--';
  }

  private removeProjectFromList(): void {
    const projectIndex = this.projectList.findIndex((project) => project === this.project);
    this.projectList.splice(projectIndex, 2);
    this.projectIndex = this.projectList.findIndex((project) => project === this.project);
  }

  private resetPanelData(): void {
    this.projectList.splice(0, 1);
    this.projectIndex = -1;
    this.panelList = [];
  }
}
